import React, { useEffect, useState, useRef } from "react";
import DatePicker from "react-datepicker";
import { Oval } from "react-loader-spinner";

import Icon from "../DesignSystem/Icons/Icon";
import colours from "../DesignSystem/Colours";

import "react-datepicker/dist/react-datepicker.css";

export const EditableCell = ({
    value: initialValue = '',
    row,
    column,
    updateData,
    placeholder = '',
    defaultSuccessMessage = 'Successfully updated this resource.',
    defaultErrorMessage = 'There was an error updating this resource.',
    modifier = null,
    isDatePicker = false,
    dateFormat = 'MMM dd, yyyy',
}) => {
    const [cleanInitialValue, setCleanInitialValue] = useState(initialValue);
    const [value, setValue] = useState(initialValue);
    const [showButtons, setShowButtons] = useState(false);
    const [loading, setLoading] = useState(false);

    const inputRef = useRef();

    const attribute = column.id;
    const inventory = row.original;

    useEffect(() => {
        setValue(initialValue);
        setCleanInitialValue(initialValue);

        if (!initialValue) {
		    const attributeName = attribute?.split('.')[1] || attribute; // attribute can be "additionalData.name" or "name"
            const foundAdditionalData = inventory.additionalData.find(data => data.name === attributeName);

            if (foundAdditionalData) {
                const parsedValue = isDatePicker ? new Date(foundAdditionalData.value) : foundAdditionalData.value;
                setValue(parsedValue);
                setCleanInitialValue(parsedValue);
            }
        }
    }, [initialValue]);

    const handleChange = value => {
        const parsedValue = modifier ? modifier(value) : value;
        setValue(parsedValue);
    }

    const handleFocus = () => {
        setShowButtons(true);
    }

    const handleBlur = async (value = null) => {
        await new Promise(resolve => setTimeout(resolve, 100));
        setShowButtons(false);
        inputRef.current?.blur();

        setValue(typeof value === 'string' ? value : cleanInitialValue);
    }

    const handleSave = async () => {
        if (cleanInitialValue === value) return;

        setLoading(true);
        try {
            await updateData(inventory, attribute, value);
            setCleanInitialValue(value);

            await new Promise(resolve => setTimeout(resolve, 300));
            await handleBlur(value);

            showStatusMsg(defaultSuccessMessage, 'success');
            setLoading(false);
        } catch (error) {
            showStatusMsg(error.message || defaultErrorMessage);
            console.error(error);
            setLoading(false);
        }
    }

    const handleCancel = async () => {
        setValue(cleanInitialValue);
        await handleBlur();
    }

    const handleKeyDown = (e) => {
        const keyActions = {
            Escape: handleCancel,
            Enter: handleSave,
        }

        if (keyActions[e.key]) {
            e.preventDefault();
            keyActions[e.key]();
        }
    }

    return (
        <div className="editable-cell">
            {isDatePicker ? (
                <DatePicker
                    selected={value}
                    onFocus={handleFocus}
                    dateFormat={dateFormat}
                    onChange={(date) => handleChange(date)}
                    placeholderText={placeholder}
                    onKeyDown={(e) => handleKeyDown(e)}
                />
            ) : (
                <input
                    ref={inputRef}
                    value={value}
                    onChange={(e) => handleChange(e.target.value)}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    onKeyDown={(e) => handleKeyDown(e)}
                />
            )}
            {showButtons && (
                <div className="buttons">
                    <button className="cancel" onClick={handleCancel}>
                        <Icon name="Close" size={18} style={{ color: colours.accent.error_red }} />
                    </button>
                    <button className="save" onClick={handleSave}>
                        {loading ? (
            				<Oval height={12} width={12} strokeWidth={4} color={colours.primary.blue} secondaryColor={colours.functional.hover_primaryblue} />
                        ) : (
                            <Icon name="Check" size={18} style={{ color: colours.accent.validate_green }} />
                        )}
                    </button>
                </div>
            )}
        </div>
    )
}